import React, { useState, useEffect, useMemo } from 'react';
import { API } from '../api/api';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';

export default function RecommendedTasksPanel({ isOpen, onClose, onSelectTask, isMobile, modalPosition }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [collapsedProjects, setCollapsedProjects] = useState(new Set());

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setError('');
    API.fetchRecommendedTasks()
      .then((data) => {
        if (data.success) {
          setTasks(data.data);
        } else {
          setError('Failed to load recommended tasks');
        }
      })
      .catch((err) => {
        console.error('Error fetching recommended tasks:', err);
        setError('Failed to load recommended tasks');
      })
      .finally(() => setLoading(false));
  }, [isOpen]);

  // Group tasks by project
  const grouped = useMemo(() => {
    const filtered = search.trim()
      ? tasks.filter(
          (t) =>
            t.name.toLowerCase().includes(search.toLowerCase()) ||
            t.projectName.toLowerCase().includes(search.toLowerCase())
        )
      : tasks;

    const groups = new Map();
    for (const task of filtered) {
      const key = task.projectId;
      if (!groups.has(key)) {
        groups.set(key, { projectId: task.projectId, projectName: task.projectName, tasks: [] });
      }
      groups.get(key).tasks.push(task);
    }
    return Array.from(groups.values());
  }, [tasks, search]);

  const toggleProject = (projectId) => {
    setCollapsedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });
  };

  if (!isOpen) return null;

  // Determine which side the EventFormModal is on, place panel on the opposite side
  // modalPosition has either { left: '20px' } or { right: '20px' }
  const modalIsOnLeft = modalPosition && 'left' in modalPosition && !('right' in modalPosition);

  const panelStyles = isMobile
    ? {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 2700,
      }
    : {
        position: 'fixed',
        top: '80px',
        // Place panel right next to the modal (modal is ~400px wide + 20px from edge + 10px gap)
        ...(modalIsOnLeft ? { left: '430px' } : { right: '430px' }),
        width: '320px',
        maxHeight: '85vh',
        zIndex: 2550,
      };

  return (
    <>
      {/* Backdrop on mobile */}
      {isMobile && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50"
          style={{ zIndex: 2699 }}
          onClick={onClose}
        />
      )}

      <div
        className="bg-[#0D1A27] rounded-lg border border-[#273C61] flex flex-col"
        style={panelStyles}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-[#273C61]">
          <div className="flex items-center gap-2">
            <StarIconSolid className="w-5 h-5 text-amber-500" />
            <h3 className="text-sm font-semibold text-white">Mine opgaver</h3>
          </div>
          <button
            onClick={onClose}
            className="text-white/40 hover:text-white p-1 transition-colors"
            aria-label="Close"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <div className="p-2 border-b border-[#273C61]">
          <div className="relative">
            <MagnifyingGlassIcon className="w-4 h-4 text-white/40 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Søg opgaver eller projekter..."
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-[#273C61] bg-[#111e2e] text-white rounded-md focus:outline-none focus:ring-1 focus:ring-[#FF7800] focus:border-[#FF7800]"
              style={{ fontSize: '16px' }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-2" style={{ minHeight: 0 }}>
          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#FF7800]" />
              <span className="ml-2 text-sm text-white/50">Henter opgaver...</span>
            </div>
          )}

          {error && (
            <div className="text-red-500 text-sm text-center py-4">{error}</div>
          )}

          {!loading && !error && grouped.length === 0 && (
            <div className="text-white/40 text-sm text-center py-8">
              {search ? 'Ingen opgaver matcher søgningen' : 'Ingen aktive opgaver fundet'}
            </div>
          )}

          {!loading &&
            !error &&
            grouped.map((group) => {
              const isCollapsed = collapsedProjects.has(group.projectId);
              return (
                <div key={group.projectId} className="mb-1">
                  {/* Project header */}
                  <button
                    onClick={() => toggleProject(group.projectId)}
                    className="w-full flex items-center gap-1.5 px-2 py-1.5 text-xs font-semibold text-white/60 uppercase tracking-wide hover:bg-[#273C61] rounded transition-colors"
                  >
                    <span
                      className="text-white/40 transition-transform"
                      style={{ transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}
                    >
                      ▾
                    </span>
                    <span className="truncate flex-1 text-left">{group.projectName}</span>
                    <span className="text-white/40 font-normal">({group.tasks.length})</span>
                  </button>

                  {/* Task list */}
                  {!isCollapsed && (
                    <div className="ml-2">
                      {group.tasks.map((task) => (
                        <button
                          key={task.id}
                          onClick={() => onSelectTask(task)}
                          className="w-full text-left px-2 py-1.5 text-sm text-white/80 hover:bg-[#FF7800]/10 hover:text-[#FF7800] rounded transition-colors truncate flex items-center gap-2"
                          title={`${task.name} — ${group.projectName}`}
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />
                          <span className="truncate">{task.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
        </div>

        {/* Footer */}
        <div className="border-t border-[#273C61] px-3 py-2 text-xs text-white/40 text-center">
          {tasks.length} aktive opgaver · In Progress
        </div>
      </div>
    </>
  );
}
